import ContextMenu from '@/components/common/ContextMenu';
import InstrumentInfoContext from '@/components/common/InstrumentInfoContext';
import Loader from '@/components/common/Loader';
import ValueBreakdownContext, { ValueBreakdownContextData } from '@/components/common/ValueBreakdownContext';
import SafeInput from '@/components/inputs/SafeInput';
import ClosePositionModal from '@/components/modal/ClosePositionModal';
import VirtualizedTable from '@/components/table/VirtualizedTable';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { OrderPrefillValues } from '@/state/reducers/orderSlice';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { UseDisclosureReturn } from '@/utils/hooks/useDisclosure';
import useNopPositionTable from '@/utils/hooks/useNopPositionTable';
import { NopPosition } from '@/utils/providers/PositionsProvider';
import { Fragment, memo, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import PositionGroupSwitch, { GroupBy } from './PositionGroupSwitch';

export interface NopPositionRow
    extends Modify<
        NopPosition,
        {
            unrealisedPnl: {
                value: number;
                currency: string;
            };
            unrealisedPnlInBaseCurrency: {
                value: number | null;
                currency: string;
            };
            unsettledPnL: {
                value: number;
                currency: string;
            };
            unsettledPnLInBaseCurrency: {
                value: number;
                currency: string;
            };
            instrument: string;
            ccy1?: string;
            ccy2?: string;
            tableInstrument: string;
            market: PairMap;
            type: string;
            openPrice: number;
            currentPrice: number;
            settlementDate: string;
            accountCode: string;
            realisedPnLCurrency: string;
        }
    > {}

export interface NopPositionTableProps {
    setCSVData: React.Dispatch<React.SetStateAction<any[]>>;
    onOrderModal: (row: NopPositionRow) => void;
    prefillValues?: OrderPrefillValues;
    setPrefillValues?: React.Dispatch<React.SetStateAction<OrderPrefillValues | undefined>>;
    orderDisclosure: UseDisclosureReturn;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const NopPositionTable = (props: NopPositionTableProps) => {
    const { prefillValues, setPrefillValues, orderDisclosure } = props;

    const contextMenu = useContextMenu();
    const currentAccount = useAppSelector(selectCurrentAccount);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    const [groupBy, setGroupBy] = useState<GroupBy>('Products');
    const [stateLoading, setStateLoading] = useState<boolean>(false);
    const [instrumentInfoContext, setInstrumentInfoContext] = useState();
    const [valueBreakdownContext, setValueBreakdownContext] = useState<ValueBreakdownContextData>();

    const { debouncedTableInstance, debouncedNopPositions, expandedRows, globalFilter } = useNopPositionTable({
        ...props,
        groupBy,
        setInstrumentInfoContext,
        setValueBreakdownContext,
        handleOpenContextMenu
    });

    useDidUpdate(() => {
        setStateLoading(true);
        setTimeout(() => setStateLoading(false), 1000);
    }, [currentAccount]);

    return (
        <Fragment>
            <div className="flex flex-col h-full gap-2">
                <div className="flex items-center justify-between gap-2 flex-wrap-reverse">
                    <div className="flex gap-2 whitespace-nowrap">
                        <div className="relative flex items-center">
                            <MdSearch className="absolute left-2 text-brand-neutral-400 z-10 w-5 h-5" />
                            <SafeInput
                                className="w-48 text-xs rounded-none pl-8 py-1.5"
                                placeholder="Search..."
                                value={globalFilter || ''}
                                onChange={(e) => debouncedTableInstance.setGlobalFilter(e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <PositionGroupSwitch groupBy={groupBy} setGroupBy={setGroupBy} />
                    </div>
                </div>
                <div className="flex-1 basis-0 overflow-hidden">
                    {stateLoading ? (
                        <div className="m-2 mt-0">
                            <Loader />
                        </div>
                    ) : (
                        <MemoizedNopPositionTable
                            groupBy={groupBy}
                            expandedRows={expandedRows}
                            globalFilter={globalFilter}
                            tableInstance={debouncedTableInstance}
                            debouncedNopPositions={debouncedNopPositions}
                            currentAccount={currentAccount}
                        />
                    )}
                </div>
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 4, y: 4 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className={
                    valueBreakdownContext
                        ? 'p-2 text-xs text-brand-neutral-200 !bg-background-dark font-normal max-w-xs'
                        : '!bg-brand-background !w-auto'
                }>
                {valueBreakdownContext ? (
                    <ValueBreakdownContext {...valueBreakdownContext} />
                ) : (
                    <InstrumentInfoContext
                        market={instrumentInfoContext}
                        handleCloseContextMenu={handleCloseContextMenu}
                    />
                )}
            </ContextMenu>
            <ClosePositionModal
                debouncedNopPositions={debouncedNopPositions}
                prefillValues={prefillValues}
                setPrefillValues={setPrefillValues}
                opened={orderDisclosure[0]}
                handlers={orderDisclosure[1]}
            />
        </Fragment>
    );
};

export default NopPositionTable;

const MemoizedNopPositionTable = memo(
    ({ tableInstance, debouncedNopPositions, expandedRows, globalFilter, currentAccount }: any) => {
        // globalFilterEnabled
        return <VirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
    }
);

MemoizedNopPositionTable.displayName = 'NopPositionTable';
